import React, { useEffect, useState } from "react";

import { parse, subMinutes, format } from "date-fns";

import { Button as MuiButton, Grid, InputAdornment } from "@material-ui/core";

import {
  ArrayInput,
  BooleanInput,
  FormDataConsumer,
  maxValue,
  minValue,
  number,
  NumberInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { generateTimeInterval } from "../../../utils/dateUtils";
import { truncateText } from "../../../utils/utils";
import { Scheduler } from "../../../components/common/scheduler/Scheduler";
import { AppType, WorkflowChoices } from "../../../utils/constants";
import { AssetTypeCreateDialog } from "../assetTypes/AssetTypeCreateDialog";
import {
  validateDurationAndBuffer,
  validateEndTime,
  validatePositiveNumber,
  validateStartTime,
} from "./EventTypeUtils";
import { fetchAreas, fetchAssetTypes } from "../reloadYard/ReloadYardUtils";

const validateMaxBookings = (maxBookings) => [
  required(),
  number(),
  minValue(1, "Must be 1 or greater."),
  maxValue(maxBookings, `Exceeds Asset max bookings of ${maxBookings}.`),
];

const validateMaxFuture = (maxDays) => [
  required(),
  number(),
  maxValue(maxDays, `Exceeds ${maxDays} days.`),
];

const getItemLabel = () => null;
const getLastBookingTime = (formData, scopedFormData) => {
  if (!scopedFormData?.selected || !scopedFormData.endDayTime) return false;

  const { duration = 0, bufferTime = 0 } = formData;
  const totalSlotLength = duration + bufferTime;

  // Parse endDayTime, expected format like "4:00pm"
  const endTime = parse(
    scopedFormData.endDayTime.toLowerCase(),
    "h:mmaaa",
    new Date(),
  );

  const lastBookingTime = subMinutes(endTime, totalSlotLength);
  const formatted = format(lastBookingTime, "h:mmaaa");

  return `Last booking: ${formatted}`;
};

const isYardDayDisabled = (yardDay) => {
  return yardDay ? !yardDay.selected : true;
};

const assetTypeOptionRenderer = (record) => {
  if (!record) return "";
  let name = truncateText(record.name, 20);
  // Pad with spaces to ensure it always takes up exactly 25 characters
  name = name.padEnd(25, "\u00A0");
  return `${name} (Max: ${record.maxConcurrentBookings})`;
};

export const EventTypeForm = (props) => {
  const form = useForm();
  const notify = useNotify();
  const [assetTypes, setAssetTypes] = useState(
    window.appUser.reloadYard.assets || [],
  );
  const [areas, setAreas] = useState(
    () =>
      window.appUser.reloadYard.areas.items.filter(
        (itm) => itm.active === "active",
      ),
    [],
  );
  const { record } = props;
  const [selectedAssetType, setSelectedAssetType] = useState(record?.assetType);
  const [previewOpen, setPreviewOpen] = useState(false);
  const yardSchedule = window.appUser.reloadYard.schedule;

  useEffect(() => {
    fetchAreas(setAreas, notify);
    fetchAssetTypes(setAssetTypes, notify);
  }, []);

  const handleAssetTypeChange = (event) => {
    let selectedAssetTypeId;
    let nweAssetType;
    debugger;

    // Determine if event is an event or a direct string ID
    if (typeof event === "string" && selectedAssetType) {
      selectedAssetTypeId = event; // If it's a string, use it directly (inline Create)
      nweAssetType = selectedAssetType;
    } else if (event?.target?.value) {
      selectedAssetTypeId = event?.target?.value; // If it's an event, extract the value (onSelect)
      nweAssetType = assetTypes.find((itm) => itm.id === selectedAssetTypeId);
    }

    form.change("resourceName", nweAssetType?.name || "");
    form.change("numberOfResources", nweAssetType?.maxConcurrentBookings || 1);
    setSelectedAssetType(nweAssetType);
  };

  const handlePreview = (formData, notify) => {
    if (
      formData.name &&
      formData.numberOfResources &&
      formData.schedule?.minNoticeHours &&
      formData.schedule?.maxDaysIntoFuture
    ) {
      setPreviewOpen(true);
    } else {
      notify("Complete the Event form to open preview.", "warning");
    }
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              {/* <Grid item xs={6}>
                <SelectInput
                  source="areaId"
                  label="Area"
                  variant="outlined"
                  choices={areas}
                  allowEmpty
                  fullWidth
                  helperText={false}
                />
              </Grid> */}
              {/* {!formData.assetTypeId && (
                <Grid item xs={6}>
                  <TextInput
                    label="Resource Name"
                    source="resourceName"
                    variant="outlined"
                    fullWidth
                    disabled={formData.assetTypeId}
                    helperText={false}
                  />
                </Grid>
              )} */}
              <Grid item xs={6}>
                <TextInput
                  label="Event Name"
                  source="name"
                  variant="outlined"
                  fullWidth
                  validate={required()}
                  helperText={false}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectInput
                  source="workflow"
                  label="Workflow"
                  variant="outlined"
                  choices={WorkflowChoices}
                  fullWidth
                  allowEmpty
                  helperText={false}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <NumberInput
                  label="Duration"
                  source="duration"
                  variant="outlined"
                  fullWidth
                  step={15}
                  min={0}
                  validate={[validatePositiveNumber, validateDurationAndBuffer]}
                  helperText={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mins</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInput
                  label="Buffer Time"
                  source="bufferTime"
                  min={0}
                  step={15}
                  variant="outlined"
                  fullWidth
                  validate={[validatePositiveNumber, validateDurationAndBuffer]}
                  helperText={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mins</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <SelectInput
                  source="assetTypeId"
                  label="Asset"
                  variant="outlined"
                  choices={assetTypes}
                  optionText={assetTypeOptionRenderer}
                  optionValue="id"
                  createLabel="Create a new Assett..."
                  create={
                    <AssetTypeCreateDialog
                      areas={areas}
                      assets={assetTypes}
                      setAssets={setAssetTypes}
                      setSelectedAsset={setSelectedAssetType}
                    />
                  }
                  onChange={handleAssetTypeChange}
                  allowEmpty
                  resettable
                  fullWidth
                  helperText="Most constrained resource"
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInput
                  label="Max. Concurrent Bookings"
                  source="numberOfResources"
                  variant="outlined"
                  fullWidth
                  min={1}
                  max={selectedAssetType?.maxConcurrentBookings || Infinity}
                  validate={validateMaxBookings(
                    selectedAssetType?.maxConcurrentBookings || Infinity,
                  )}
                  helperText={false}
                />
              </Grid>

              <Grid item xs={6}>
                <NumberInput
                  source="schedule.minNoticeHours"
                  label="Min. Notice (Hours)"
                  min={0}
                  variant="outlined"
                  fullWidth
                  validate={validatePositiveNumber}
                  helperText={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Hours</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInput
                  source="schedule.maxDaysIntoFuture"
                  label="Max. Days into future"
                  min={0}
                  max={365}
                  variant="outlined"
                  fullWidth
                  validate={validateMaxFuture(365)}
                  helperText={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">Days</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source="description"
                  label="Description"
                  variant="outlined"
                  multiline
                  minRows={2}
                  fullWidth
                  validate={required()}
                  helperText="Message to customers"
                />
              </Grid>
              <Grid item xs={12}>
                <MuiButton
                  variant="outlined"
                  color="primary"
                  onClick={() => handlePreview(formData, notify)}
                >
                  PREVIEW
                </MuiButton>
                {previewOpen && (
                  <Scheduler
                    eventType={formData}
                    appType={AppType.CUSTOMER}
                    open={previewOpen}
                    setIsOpen={setPreviewOpen}
                    resettable={false}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <ArrayInput
              source={"schedule.workdays"}
              label="Work days"
              fullWidth
            >
              <SimpleFormIterator
                disableAdd
                disableRemove
                disableReordering
                getItemLabel={getItemLabel}
              >
                <FormDataConsumer>
                  {({
                    formData, // The whole form data
                    scopedFormData, // The data for this item of the ArrayInput
                    getSource, // Function to get the valid source inside an ArrayInput.
                    ...rest
                  }) => {
                    const yardDay = yardSchedule.workdays.find(
                      (day) => day.name === scopedFormData?.name,
                    );
                    const timeIntervals = generateTimeInterval(
                      yardDay?.startDayTime,
                      yardDay?.endDayTime,
                    );

                    return (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <BooleanInput
                            source={getSource("selected")}
                            label={scopedFormData?.name}
                            disabled={isYardDayDisabled(yardDay)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <SelectInput
                            label="Start Time"
                            source={getSource("startDayTime")}
                            variant="outlined"
                            fullWidth
                            helperText={false}
                            choices={timeIntervals}
                            disabled={!scopedFormData?.selected}
                            validate={[
                              (value, allValues) =>
                                validateStartTime(value, allValues, getSource),
                            ]}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <SelectInput
                            label="End Time"
                            source={getSource("endDayTime")}
                            variant="outlined"
                            fullWidth
                            choices={timeIntervals}
                            disabled={!scopedFormData?.selected}
                            helperText={getLastBookingTime(
                              formData,
                              scopedFormData,
                            )}
                            validate={[
                              (value, allValues) =>
                                validateEndTime(value, allValues, getSource),
                            ]}
                          />
                        </Grid>
                      </Grid>
                    );
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};
